import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError } from '../lib/api'
import { Banner } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/**
 * The workspace's own LLM key — "bring your own key" rather than a Wami-run shared model, so
 * the chat-scoped AI assistant (starting with conversation summaries, see Inbox) can exist
 * without Wami operating or paying for a model backend. One key at a time, verified with a
 * cheap call before it is ever saved.
 */
export function AiSettings() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [model, setModel] = useState('')

  const data = useQuery({ queryKey: ['ai-settings'], queryFn: api.aiSettings, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ai-settings'] })

  const save = useMutation({
    mutationFn: () =>
      api.saveAiSettings({
        apiKey: apiKey.trim() || undefined,
        baseUrl: baseUrl.trim() || undefined,
        model: model.trim() || undefined,
      }),
    onSuccess: () => {
      setEditing(false)
      setApiKey('')
      void refresh()
    },
  })

  const clear = useMutation({ mutationFn: api.clearAiSettings, onSuccess: refresh })

  if (data.isPending) return <div className="cad-card" style={{ height: '160px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { configured, baseUrl: currentBaseUrl, model: currentModel, canManage } = data.data!

  const startEditing = () => {
    setApiKey('')
    setBaseUrl(configured ? currentBaseUrl : '')
    setModel(configured ? currentModel : '')
    setEditing(true)
  }

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        {!canManage && <ReadOnlyNotice what="the AI provider" />}
        <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
          <h2 className="cad-section-title">AI assistant</h2>
          {configured ? (
            <span className="cad-pill" data-tone="good">
              connected
            </span>
          ) : (
            <span className="cad-pill">not connected</span>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Add your own API key from OpenAI, Groq, OpenRouter, or any OpenAI-compatible endpoint
          (including a self-hosted one) to turn on in-chat features like summarising a
          conversation. Nothing is sent to Wami — every call goes straight from your server to
          your provider, on your own key.
        </p>

        {configured && !editing && (
          <p className="cad-note">
            Using <b>{currentModel}</b> at <b>{currentBaseUrl}</b>.
          </p>
        )}

        {canManage && !editing && (
          <div className="row gap-2 wrap">
            <button type="button" className="cad-btn" data-variant="quiet" onClick={startEditing}>
              {configured ? 'Change' : 'Connect a provider'}
            </button>
            {configured && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                disabled={clear.isPending}
                onClick={() => {
                  if (confirm(`Disconnect the AI provider?

Auto-replies, summaries and anything else that calls the model stop working until a new key is saved.`)) clear.mutate()
                }}
              >
                Disconnect
              </button>
            )}
          </div>
        )}

        {clear.error && (
          <Banner tone="error">
            {clear.error instanceof ApiError || clear.error instanceof Error
              ? clear.error.message
              : 'Could not disconnect.'}
          </Banner>
        )}

        {canManage && editing && (
          <div className="stack gap-3" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px' }}>
            <div className="field-grid">
              <div className="cad-field">
                <label htmlFor="ai-key">API key</label>
                <input
                  id="ai-key"
                  className="cad-input"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={configured ? 'Leave blank to keep the current key' : 'sk-…'}
                />
              </div>
              <div className="cad-field">
                <label htmlFor="ai-base-url">Base URL</label>
                <input
                  id="ai-base-url"
                  className="cad-input"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://api.openai.com/v1"
                />
              </div>
              <div className="cad-field">
                <label htmlFor="ai-model">Model</label>
                <input
                  id="ai-model"
                  className="cad-input"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="gpt-4o-mini"
                />
              </div>
            </div>

            {save.error && (
              <Banner tone="error">
                {save.error instanceof ApiError || save.error instanceof Error
                  ? save.error.message
                  : 'That could not be saved.'}
              </Banner>
            )}

            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                disabled={(!configured && !apiKey.trim()) || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Verifying…' : 'Save'}
              </button>
              <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
